import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE_NAME, SITE_URL, DEFAULT_DESCRIPTION, DEFAULT_OG_IMAGE, DEFAULT_LOCALE, DEFAULT_AUTHOR } from '../seoConfig';

const ensureMetaTag = (selector, attributes) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attributes).forEach(([key, value]) => {
      el.setAttribute(key, value);
    });
    document.head.appendChild(el);
  }
  return el;
};

const Seo = ({
  title,
  description,
  ogTitle,
  ogDescription,
  ogImage,
  author,
  type = 'website',
  index = true,
}) => {
  const location = useLocation();
  const path = location.pathname + location.search;
  const url = SITE_URL.replace(/\/$/, '') + path;

  useEffect(() => {
    const pageTitle = title || SITE_NAME;
    const metaDescription = description || DEFAULT_DESCRIPTION;
    const finalOgTitle = ogTitle || pageTitle;
    const finalOgDescription = ogDescription || metaDescription;
    const finalOgImage = ogImage || DEFAULT_OG_IMAGE;
    const metaAuthor = author || DEFAULT_AUTHOR;

    document.title = pageTitle;

    ensureMetaTag('meta[name="description"]', { name: 'description' }).setAttribute(
      'content',
      metaDescription,
    );

    ensureMetaTag('meta[property="og:title"]', { property: 'og:title' }).setAttribute(
      'content',
      finalOgTitle,
    );
    ensureMetaTag('meta[property="og:description"]', { property: 'og:description' }).setAttribute(
      'content',
      finalOgDescription,
    );
    ensureMetaTag('meta[property="og:image"]', { property: 'og:image' }).setAttribute(
      'content',
      finalOgImage,
    );
    ensureMetaTag('meta[property="og:url"]', { property: 'og:url' }).setAttribute(
      'content',
      url,
    );
    ensureMetaTag('meta[property="og:type"]', { property: 'og:type' }).setAttribute(
      'content',
      type,
    );
    ensureMetaTag('meta[property="og:site_name"]', { property: 'og:site_name' }).setAttribute(
      'content',
      SITE_NAME,
    );
    ensureMetaTag('meta[property="og:locale"]', { property: 'og:locale' }).setAttribute(
      'content',
      DEFAULT_LOCALE,
    );

    ensureMetaTag('meta[name="author"]', { name: 'author' }).setAttribute(
      'content',
      metaAuthor,
    );
    ensureMetaTag('meta[property="article:author"]', { property: 'article:author' }).setAttribute(
      'content',
      metaAuthor,
    );

    ensureMetaTag('meta[name="twitter:card"]', { name: 'twitter:card' }).setAttribute(
      'content',
      'summary_large_image',
    );
    ensureMetaTag('meta[name="twitter:title"]', { name: 'twitter:title' }).setAttribute(
      'content',
      finalOgTitle,
    );
    ensureMetaTag('meta[name="twitter:description"]', { name: 'twitter:description' }).setAttribute(
      'content',
      finalOgDescription,
    );
    ensureMetaTag('meta[name="twitter:image"]', { name: 'twitter:image' }).setAttribute(
      'content',
      finalOgImage,
    );

    const robotsContent = index ? 'index,follow' : 'noindex,nofollow';
    ensureMetaTag('meta[name="robots"]', { name: 'robots' }).setAttribute(
      'content',
      robotsContent,
    );

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);
  }, [title, description, ogTitle, ogDescription, ogImage, author, type, index, path, url]);

  return null;
};

export default Seo;
